import {
  LinkedInMonoIcon,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  buildLinkedInShareOffsiteUrl,
  linkedinShareEnabled,
  defaultLinkedInShareCaption,
  fetchLinkedInShareStatus,
  postJobToLinkedIn,
  startLinkedInShareConnect,
} from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import type { OrganisationOpportunity } from '../../hooks/use-organisation-opportunities'

type LinkedInShareJobDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunity: OrganisationOpportunity
  shareUrl: string
  token: string | null
}

export function LinkedInShareJobDialog({
  open,
  onOpenChange,
  opportunity,
  shareUrl,
  token,
}: LinkedInShareJobDialogProps) {
  const [caption, setCaption] = useState('')
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [posting, setPosting] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [tokenValid, setTokenValid] = useState(false)

  const featureEnabled = linkedinShareEnabled()
  const orgName = opportunity.organisation_name ?? 'our team'
  const offsiteUrl = buildLinkedInShareOffsiteUrl(shareUrl)
  const useIntegratedShare = featureEnabled && connected && tokenValid && !loadingStatus

  useEffect(() => {
    if (!open) return
    setCaption(
      defaultLinkedInShareCaption({
        title: opportunity.title,
        organisationName: orgName,
        shareUrl,
      }),
    )
  }, [open, opportunity.title, orgName, shareUrl])

  useEffect(() => {
    if (!open || !token || !featureEnabled) return
    let cancelled = false
    setLoadingStatus(true)
    void fetchLinkedInShareStatus(token)
      .then((status) => {
        if (cancelled) return
        setConnected(status.connected)
        setTokenValid(status.token_valid)
      })
      .catch(() => {
        if (!cancelled) {
          setConnected(false)
          setTokenValid(false)
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStatus(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, token, featureEnabled])

  const handleConnect = async () => {
    if (!token) return
    setConnecting(true)
    try {
      const returnTo = `${window.location.pathname}${window.location.search}`
      const authorizeUrl = await startLinkedInShareConnect(token, returnTo)
      window.location.href = authorizeUrl
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not connect LinkedIn')
      setConnecting(false)
    }
  }

  const handlePost = async () => {
    if (!token) return
    setPosting(true)
    try {
      await postJobToLinkedIn({
        token,
        opportunityId: opportunity.id,
        caption,
      })
      toast.success('Posted to your LinkedIn profile')
      onOpenChange(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not post to LinkedIn')
    } finally {
      setPosting(false)
    }
  }

  const description = useIntegratedShare
    ? 'Post this job to your LinkedIn profile using your connected account.'
    : 'You will open LinkedIn to share this job. Copy the message below and paste it into your post if you like.'

  const captionHintId = 'linkedin-share-caption-hint'
  const captionHint = useIntegratedShare
    ? 'This text will be posted to your LinkedIn profile.'
    : 'LinkedIn opens with the job link only — copy this message to paste into your post.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkedInMonoIcon className="h-5 w-5 shrink-0" />
            Share to LinkedIn
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Textarea
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            rows={5}
            className="resize-y"
            aria-label="LinkedIn post message"
            aria-describedby={captionHintId}
          />
          <p id={captionHintId} className="text-xs text-muted-foreground">
            {captionHint}
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
          {loadingStatus && featureEnabled ? (
            <Button type="button" disabled>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Checking LinkedIn connection…
            </Button>
          ) : useIntegratedShare ? (
            <Button type="button" onClick={() => void handlePost()} disabled={posting || !caption.trim()}>
              {posting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Posting…
                </>
              ) : (
                'Post to my LinkedIn profile'
              )}
            </Button>
          ) : offsiteUrl ? (
            <Button type="button" asChild>
              <a href={offsiteUrl} target="_blank" rel="noopener noreferrer">
                Continue on LinkedIn
              </a>
            </Button>
          ) : null}

          {featureEnabled && !loadingStatus && !useIntegratedShare && token ? (
            <Button
              type="button"
              variant="link"
              className="h-auto px-0 text-sm text-muted-foreground"
              onClick={() => void handleConnect()}
              disabled={connecting}
            >
              {connecting ? 'Redirecting to LinkedIn…' : 'Connect LinkedIn to post from TalentCo in one step'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
